/**
 * Timer V2 — the allocation review client boundary (8E).
 *
 * ============================================================================
 * WHAT THIS IS
 * ============================================================================
 * A work episode that has been stopped holds MEASURED time and a FROZEN
 * PROPOSAL for how that time divides across the jobs the technician selected.
 * Neither is a decision. The decision is a human one: someone authorized looks
 * at the proposal and accepts a distribution. Only then does the time become
 * accepted allocation and reach `ticket_jobs.actual_minutes`.
 *
 * This module is the only place the browser talks to that workflow:
 *
 *     readTimerEpisodeReview(workEpisodeId)      -> the frozen proposal
 *     finalizeTimerEpisodeReview(id, allocations) -> accept a distribution
 *     acceptTrivialTimerReview(workEpisodeId)    -> #3: let the SERVER accept a
 *                                                   review that leaves nothing to
 *                                                   decide (U0/U1); else Review Time
 *
 * ============================================================================
 * WHAT THE BROWSER IS NOT TRUSTED WITH
 * ============================================================================
 * The proposal is never re-derived here. There is no client-side split, no
 * expected-minutes fallback, no "if the server didn't say, assume". Everything
 * displayed comes from `get_my_timer_episode_review`, which is also the only
 * thing that authorizes the read — the frozen review tables grant the browser
 * no direct SELECT at all (8E revoked it, because shop-scoped read let any
 * technician see a colleague's measured timing).
 *
 * On submit the browser sends an episode id and a complete list of
 * `{job_id, final_minutes}`. It does NOT send shop, ticket, technician,
 * review id, measured minutes, the attributable total, the actor or a
 * timestamp. The server derives every one of those and validates the total
 * against ITS OWN frozen review, so a tampered client cannot widen what it is
 * allowed to allocate.
 *
 * `p_complete_job_ids` is ALWAYS empty. Stopping work is not finishing a job,
 * and accepting an allocation is not either — it says only "this is how my
 * measured time divides". Job completion is a separate authority with its own
 * unsolved concurrency, and this workflow must never quietly trigger it.
 */
import { supabase } from '@/integrations/supabase/client';

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{
  data: unknown;
  error: { message: string } | null;
}>;

/** These RPCs are newer than the generated types, so the cast is narrow. */
// RECEIVER-PRESERVING. `SupabaseClient.rpc` is a prototype method whose body is
// `return this.rest.rpc(...)`, so detaching it —
//
//     const rpc = supabase.rpc as unknown as Rpc;   // <- what this used to be
//
// — drops the receiver and throws `TypeError: Cannot read properties of
// undefined (reading 'rest')` before the request is ever sent. That is not
// theoretical: it is what production returned for the timer state read and the
// ticket delete, captured with http_status = null because nothing was sent.
//
// The wrapper below calls the method ON the client, so the receiver survives.
// Every call site is unchanged.
const rpc: Rpc = (fn, args) => (supabase.rpc as unknown as Rpc)(fn, args);

/**
 * How a job's suggested minutes were arrived at. Exactly one of these is
 * non-zero per job per segment, and `suggested` is their sum — the technician
 * is entitled to know whether a number was measured directly or split.
 */
export interface TimerReviewJob {
  jobId: string;
  /** DISPLAY ONLY. Read from the current job row, never frozen into evidence. */
  jobName: string;
  suggestedMinutes: number;
  directMinutes: number;
  autoProportionalMinutes: number;
  fallbackMinutes: number;
}

/**
 * The lifecycle answers the reader can give. These are OUTCOMES, not
 * failures: a second browser tab that lost the race must be able to render
 * "already reviewed", not an error.
 */
export type TimerReviewOutcome =
  /** A frozen proposal exists and reconciles. Editable. */
  | 'AVAILABLE'
  /** Someone already accepted an allocation for this episode. */
  | 'ALREADY_RESOLVED'
  /** The episode is active, paused, or stopped without a review. */
  | 'NOT_AWAITING_REVIEW'
  /** Awaiting review, but the frozen proposal is missing. Needs repair. */
  | 'NO_REVIEW_SNAPSHOT'
  /** Persisted facts contradict each other. Never show an editor over these. */
  | 'INTEGRITY_ERROR'
  /**
   * Unknown, another tenant's, or one this caller may not review. Deliberately
   * ONE answer for all three — distinguishing them turns an episode id into an
   * existence oracle.
   */
  | 'EPISODE_NOT_AVAILABLE'
  /** The read itself failed. Not a statement about the episode. */
  | 'READ_FAILED';

export interface TimerEpisodeReview {
  outcome: TimerReviewOutcome;
  workEpisodeId: string;
  reviewId: string | null;
  ticketId: string | null;
  technicianId: string | null;
  /** Total measured time in the episode. */
  measuredMinutes: number;
  /** The portion tied to selected jobs. THIS is what a distribution must total. */
  attributableMinutes: number;
  /** Productive ticket-level time not tied to any selected job. Never spread. */
  unattributedMinutes: number;
  allocationAlgorithmVersion: string | null;
  jobs: TimerReviewJob[];
  /** Present for NO_REVIEW_SNAPSHOT / INTEGRITY_ERROR / READ_FAILED. */
  note: string | null;
}

const KNOWN_OUTCOMES: TimerReviewOutcome[] = [
  'AVAILABLE', 'ALREADY_RESOLVED', 'NOT_AWAITING_REVIEW',
  'NO_REVIEW_SNAPSHOT', 'INTEGRITY_ERROR', 'EPISODE_NOT_AVAILABLE',
];

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function emptyReview(
  workEpisodeId: string,
  outcome: TimerReviewOutcome,
  note: string | null = null,
): TimerEpisodeReview {
  return {
    outcome,
    workEpisodeId,
    reviewId: null,
    ticketId: null,
    technicianId: null,
    measuredMinutes: 0,
    attributableMinutes: 0,
    unattributedMinutes: 0,
    allocationAlgorithmVersion: null,
    jobs: [],
    note,
  };
}

/**
 * Read the frozen proposal for one work episode.
 *
 * This is the source used BOTH immediately after Stop and after a refresh, a
 * route change, or on a different machine. Parsing the Stop RPC's own
 * suggestion array instead would mean two proposal parsers that can disagree,
 * and only one of them would be exercised by the recovery path anyone
 * actually relies on.
 *
 * A transport failure returns READ_FAILED rather than an empty proposal —
 * zeroes would render as a legitimate "0 minutes to allocate" form.
 */
export async function readTimerEpisodeReview(
  workEpisodeId: string,
): Promise<TimerEpisodeReview> {
  const { data, error } = await rpc('get_my_timer_episode_review', {
    p_episode_id: workEpisodeId,
  });

  if (error) {
    console.warn('[readTimerEpisodeReview] failed', error);
    return emptyReview(workEpisodeId, 'READ_FAILED', error.message);
  }
  if (!data || typeof data !== 'object') {
    return emptyReview(workEpisodeId, 'READ_FAILED', 'empty response');
  }

  const r = data as Record<string, unknown>;
  const outcome = KNOWN_OUTCOMES.includes(r.outcome as TimerReviewOutcome)
    ? (r.outcome as TimerReviewOutcome)
    : 'READ_FAILED';

  if (outcome !== 'AVAILABLE') {
    return emptyReview(workEpisodeId, outcome, (r.note as string) ?? null);
  }

  const rawJobs = Array.isArray(r.jobs) ? (r.jobs as Record<string, unknown>[]) : [];

  return {
    outcome,
    workEpisodeId: (r.work_episode_id as string) ?? workEpisodeId,
    reviewId: (r.review_id as string) ?? null,
    ticketId: (r.ticket_id as string) ?? null,
    technicianId: (r.technician_id as string) ?? null,
    measuredMinutes: num(r.measured_minutes),
    attributableMinutes: num(r.attributable_minutes),
    unattributedMinutes: num(r.unattributed_minutes),
    allocationAlgorithmVersion: (r.allocation_algorithm_version as string) ?? null,
    jobs: rawJobs.map((j) => ({
      jobId: String(j.job_id ?? ''),
      jobName: String(j.job_name ?? '') || 'Job',
      suggestedMinutes: num(j.suggested_minutes),
      directMinutes: num(j.direct_minutes),
      autoProportionalMinutes: num(j.auto_proportional_minutes),
      fallbackMinutes: num(j.fallback_minutes),
    })),
    note: null,
  };
}

/** One job's accepted minutes, exactly as the server expects them. */
export interface TimerAllocationEntry {
  job_id: string;
  final_minutes: number;
}

export type FinalizeReviewOutcome =
  /** This submission accepted the allocation. */
  | 'RESOLVED'
  /** It was already accepted — by another tab, or by a retry that did land. */
  | 'ALREADY_RESOLVED'
  /** The episode is not awaiting review (active, paused, stopped-unreviewed). */
  | 'ACTIVE' | 'PAUSED' | 'NOT_AWAITING_REVIEW'
  /** Awaiting review with no frozen proposal. */
  | 'NO_REVIEW_SNAPSHOT'
  /** Unknown / foreign / unauthorized — one answer, as with the reader. */
  | 'EPISODE_NOT_AVAILABLE'
  | 'INTEGRITY_ERROR'
  /**
   * The call did not produce a server answer. CRITICALLY this does NOT mean
   * nothing committed — the write may have succeeded with the response lost.
   * The only safe response is to retry, which is idempotent.
   */
  | 'SUBMIT_FAILED';

export interface FinalizeReviewResult {
  outcome: FinalizeReviewOutcome;
  workEpisodeId: string;
  allocatedMinutes: number | null;
  note: string | null;
}

/**
 * Whether an outcome means the episode is reviewed, whoever did it.
 *
 * `ALREADY_RESOLVED` counts. If a retry after a lost response reports that
 * the episode is already resolved, the correct thing to show the technician
 * is success — the work IS reviewed. Treating it as an error is how a user
 * ends up submitting again, or worse, believing their time was lost.
 */
export function isReviewSettled(outcome: FinalizeReviewOutcome): boolean {
  return outcome === 'RESOLVED' || outcome === 'ALREADY_RESOLVED';
}

/**
 * Accept a COMPLETE distribution for one work episode.
 *
 * `allocations` must state every reviewed job, including any the technician
 * left at zero — the server rejects a partial payload precisely because
 * omitted-job semantics are what let a distribution pass conservation while
 * leaving stale rows behind.
 *
 * Retrying is safe and is the intended recovery from an unknown network
 * result: the second attempt returns ALREADY_RESOLVED and writes nothing.
 */
export async function finalizeTimerEpisodeReview(
  workEpisodeId: string,
  allocations: TimerAllocationEntry[],
): Promise<FinalizeReviewResult> {
  const { data, error } = await rpc('finalize_my_timer_episode', {
    p_episode_id: workEpisodeId,
    p_final_allocations: allocations,
    // ALWAYS empty. Accepting an allocation completes nothing.
    p_complete_job_ids: [],
  });

  if (error) {
    console.warn('[finalizeTimerEpisodeReview] failed', error);
    return {
      outcome: 'SUBMIT_FAILED',
      workEpisodeId,
      allocatedMinutes: null,
      note: error.message,
    };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  const known: FinalizeReviewOutcome[] = [
    'RESOLVED', 'ALREADY_RESOLVED', 'ACTIVE', 'PAUSED', 'NOT_AWAITING_REVIEW',
    'NO_REVIEW_SNAPSHOT', 'EPISODE_NOT_AVAILABLE', 'INTEGRITY_ERROR',
  ];
  const outcome = known.includes(r.outcome as FinalizeReviewOutcome)
    ? (r.outcome as FinalizeReviewOutcome)
    : 'SUBMIT_FAILED';

  return {
    outcome,
    workEpisodeId: (r.work_episode_id as string) ?? workEpisodeId,
    allocatedMinutes:
      r.allocated_minutes === undefined || r.allocated_minutes === null
        ? null
        : num(r.allocated_minutes),
    note: (r.note as string) ?? null,
  };
}

// ── #3 — deterministic reviews resolve without a person (U0 / U1) ───────────
/**
 * What `accept_trivial_timer_review` decided for one episode.
 *
 *   AUTO_RESOLVED     the frozen review left nothing to decide; the server
 *                     accepted it (AUTO_ZERO: no attributable minute and no
 *                     job; AUTO_SINGLE_JOB: one job takes all of it)
 *   ALREADY_RESOLVED  someone (or an earlier attempt) already resolved it
 *   REVIEW_REQUIRED   anything else — an ambiguous allocation, AND every
 *                     failure or unexpected answer. Fail safe: the caller
 *                     opens the ordinary Review Time, exactly as before.
 *
 * The browser decides nothing here: the server classifies from its own frozen
 * review and writes through finalize_my_timer_episode with no completion ids.
 * Minutes and the job name below are the SERVER's, never computed locally.
 */
export type TrivialReviewResult =
  | {
      kind: 'AUTO_RESOLVED';
      resolution: 'AUTO_ZERO' | 'AUTO_SINGLE_JOB';
      workEpisodeId: string;
      measuredMinutes: number | null;
      attributableMinutes: number | null;
      jobName: string | null;
    }
  | { kind: 'ALREADY_RESOLVED'; workEpisodeId: string }
  | { kind: 'REVIEW_REQUIRED'; workEpisodeId: string; reason: string };

const finiteOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

export async function acceptTrivialTimerReview(workEpisodeId: string): Promise<TrivialReviewResult> {
  let data: unknown;
  try {
    const res = await rpc('accept_trivial_timer_review', { p_episode_id: workEpisodeId });
    if (res.error) {
      console.warn('[acceptTrivialTimerReview] failed', res.error);
      return { kind: 'REVIEW_REQUIRED', workEpisodeId, reason: 'REQUEST_FAILED' };
    }
    data = res.data;
  } catch (e) {
    console.warn('[acceptTrivialTimerReview] failed', e);
    return { kind: 'REVIEW_REQUIRED', workEpisodeId, reason: 'REQUEST_FAILED' };
  }
  const r = (data ?? {}) as Record<string, unknown>;
  if (r.outcome === 'AUTO_RESOLVED' && (r.resolution === 'AUTO_ZERO' || r.resolution === 'AUTO_SINGLE_JOB')) {
    return {
      kind: 'AUTO_RESOLVED',
      resolution: r.resolution,
      workEpisodeId,
      measuredMinutes: finiteOrNull(r.measured_minutes),
      attributableMinutes: finiteOrNull(r.attributable_minutes),
      jobName: typeof r.job_name === 'string' && r.job_name.trim() ? r.job_name : null,
    };
  }
  if (r.outcome === 'ALREADY_RESOLVED') return { kind: 'ALREADY_RESOLVED', workEpisodeId };
  return { kind: 'REVIEW_REQUIRED', workEpisodeId, reason: typeof r.outcome === 'string' ? r.outcome : 'UNKNOWN' };
}

/**
 * The one line the technician sees instead of Review Time, or null for none.
 *   AUTO_SINGLE_JOB              "42 min recorded to Brake Diagnosis"
 *   AUTO_ZERO with measured time "No job time to record."
 *   AUTO_ZERO with nothing       (silent)
 */
export function trivialReviewNotice(r: TrivialReviewResult): string | null {
  if (r.kind !== 'AUTO_RESOLVED') return null;
  if (r.resolution === 'AUTO_SINGLE_JOB') {
    if (r.attributableMinutes === null) return null;
    return r.jobName
      ? `${r.attributableMinutes} min recorded to ${r.jobName}`
      : `${r.attributableMinutes} min recorded`;
  }
  return (r.measuredMinutes ?? 0) > 0 ? 'No job time to record.' : null;
}
